import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaDownload, FaFileAlt, FaVideo, FaCalculator, FaBook, FaChartBar, FaRobot, FaBrain, FaEye } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../../components/SEO';
import Spinner from '../../components/Spinner/Spinner';
import './Resources.css';

// Default resources data (fallback)
const defaultResources = [
  {
    id: 'rpa-guide',
    category: 'guides',
    type_en: 'Guide',
    type_fr: 'Guide',
    title_en: 'Complete RPA Implementation Guide',
    title_fr: 'Guide Complet d\'Implémentation RPA',
    description_en: 'Step-by-step guide to implement RPA in your organization',
    description_fr: 'Guide étape par étape pour implémenter la RPA dans votre organisation',
    image: '/assets/images/finance-reports.jpg',
    downloadUrl: '#',
    pages_en: '45 pages',
    pages_fr: '45 pages',
    readTime_en: '30 min',
    readTime_fr: '30 min'
  },
  {
    id: 'roi-calculator',
    category: 'guides',
    type_en: 'Tool',
    type_fr: 'Outil',
    title_en: 'RPA ROI Calculator',
    title_fr: 'Calculateur de ROI RPA',
    description_en: 'Calculate the return on investment for your RPA projects',
    description_fr: 'Calculez le retour sur investissement de vos projets RPA',
    image: '/assets/images/data-analytics.jpg',
    downloadUrl: '#',
    pages_en: 'Excel',
    pages_fr: 'Excel',
    readTime_en: '15 min',
    readTime_fr: '15 min'
  },
  {
    id: 'best-practices',
    category: 'whitepapers',
    type_en: 'Whitepaper',
    type_fr: 'Livre Blanc',
    title_en: 'RPA Best Practices',
    title_fr: 'Meilleures Pratiques RPA',
    description_en: 'Learn the best practices for successful RPA implementation',
    description_fr: 'Découvrez les meilleures pratiques pour une implémentation RPA réussie',
    image: '/assets/images/dashboard-analytics.jpg',
    downloadUrl: '#',
    pages_en: '32 pages',
    pages_fr: '32 pages',
    readTime_en: '25 min',
    readTime_fr: '25 min'
  },
  {
    id: 'uipath-mastery',
    category: 'guides',
    type_en: 'Technical Guide',
    type_fr: 'Guide Technique',
    title_en: 'Mastering UiPath: From Beginner to Expert',
    title_fr: 'Maîtriser UiPath : De Débutant à Expert',
    description_en: 'Complete guide to learn UiPath with practical exercises and real projects',
    description_fr: 'Guide complet pour apprendre UiPath avec des exercices pratiques et des projets réels',
    image: '/assets/images/robot-technology.jpg',
    downloadUrl: '#',
    pages_en: '120 pages',
    pages_fr: '120 pages',
    readTime_en: '2h',
    readTime_fr: '2h'
  },
  {
    id: 'ai-rpa-integration',
    category: 'whitepapers',
    type_en: 'Whitepaper',
    type_fr: 'Livre Blanc',
    title_en: 'AI + RPA: The Automation Revolution',
    title_fr: 'IA + RPA : La Révolution de l\'Automatisation',
    description_en: 'How artificial intelligence transforms RPA and creates new opportunities',
    description_fr: 'Comment l\'intelligence artificielle transforme la RPA et crée de nouvelles opportunités',
    image: '/assets/images/ai-technology.jpg',
    downloadUrl: '#',
    pages_en: '28 pages',
    pages_fr: '28 pages',
    readTime_en: '20 min',
    readTime_fr: '20 min'
  },
  {
    id: 'banking-case-study',
    category: 'casestudies',
    type_en: 'Case Study',
    type_fr: 'Étude de Cas',
    title_en: 'Digital Transformation of a European Bank',
    title_fr: 'Transformation Digitale d\'une Banque Européenne',
    description_en: 'How a large bank automated 80% of its back-office processes',
    description_fr: 'Comment une grande banque a automatisé 80% de ses processus de back-office',
    image: '/assets/images/ecommerce-shopping.jpg',
    downloadUrl: '#',
    pages_en: '18 pages',
    pages_fr: '18 pages',
    readTime_en: '15 min',
    readTime_fr: '15 min'
  },
  {
    id: 'process-mining-webinar',
    category: 'webinars',
    type_en: 'Webinar',
    type_fr: 'Webinaire',
    title_en: 'Process Mining: Discovering Automation Opportunities',
    title_fr: 'Process Mining : Découvrir les Opportunités d\'Automatisation',
    description_en: '1-hour webinar on process mining techniques to identify processes to automate',
    description_fr: 'Webinaire d\'1h sur les techniques de process mining pour identifier les processus à automatiser',
    image: '/assets/images/business-clients.jpg',
    downloadUrl: '#',
    pages_en: 'Video',
    pages_fr: 'Vidéo',
    readTime_en: '60 min',
    readTime_fr: '60 min'
  },
  {
    id: 'ocr-implementation',
    category: 'guides',
    type_en: 'Practical Guide',
    type_fr: 'Guide Pratique',
    title_en: 'OCR Implementation with UiPath Document Understanding',
    title_fr: 'Implémentation OCR avec UiPath Document Understanding',
    description_en: 'Step-by-step guide to implement a performant OCR solution',
    description_fr: 'Guide step-by-step pour mettre en place une solution OCR performante',
    image: '/assets/images/ai-technology.jpg',
    downloadUrl: '#',
    pages_en: '35 pages',
    pages_fr: '35 pages',
    readTime_en: '25 min',
    readTime_fr: '25 min'
  }
];

// Kept between visits: coming back to the page shows the resources at once, refreshed in the background
let cachedResources = null;
const popularOf = (items) => items.filter(item => item.isPopular).slice(0, 3);

const Resources = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'fr' ? 'fr' : 'en';
  const [activeCategory, setActiveCategory] = useState('all');
  const [resources, setResources] = useState(cachedResources || []);
  const [popularResources, setPopularResources] = useState(cachedResources ? popularOf(cachedResources) : []);
  const [loading, setLoading] = useState(!cachedResources);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Helper function to get localized content
  const getLocalizedContent = (item, field) => {
    const localizedField = `${field}_${currentLang}`;
    const fallbackField = `${field}_en`;
    return item[localizedField] || item[fallbackField] || item[field] || '';
  };

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const q = query(collection(db, 'resources'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          cachedResources = items;
          setResources(items);
          setPopularResources(popularOf(items));
        } else {
          setResources(defaultResources);
          setPopularResources(defaultResources.slice(0, 3));
        }
      } catch (err) {
        console.error('Error fetching resources:', err);
        if (!cachedResources) {
          setResources(defaultResources);
          setPopularResources(defaultResources.slice(0, 3));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const categories = [
    { key: 'all', label: currentLang === 'fr' ? 'Toutes les ressources' : 'All Resources', icon: <FaBook /> },
    { key: 'whitepapers', label: t('resources.categories.whitepapers'), icon: <FaFileAlt /> },
    { key: 'casestudies', label: t('resources.categories.casestudies'), icon: <FaChartBar /> },
    { key: 'guides', label: t('resources.categories.guides'), icon: <FaBook /> },
    { key: 'webinars', label: t('resources.categories.webinars'), icon: <FaVideo /> }
  ];

  const filteredResources = activeCategory === 'all'
    ? resources
    : resources.filter(resource => resource.category === activeCategory);

  const getTypeIcon = (type) => {
    if (!type) return <FaFileAlt />;
    const lowerType = type.toLowerCase();
    if (lowerType.includes('guide')) return <FaBook />;
    if (lowerType.includes('livre blanc') || lowerType.includes('whitepaper')) return <FaFileAlt />;
    if (lowerType.includes('étude') || lowerType.includes('case study')) return <FaChartBar />;
    if (lowerType.includes('webinaire') || lowerType.includes('webinar')) return <FaVideo />;
    if (lowerType.includes('outil') || lowerType.includes('tool')) return <FaCalculator />;
    return <FaFileAlt />;
  };

  const getPopularIcon = (index) => {
    const icons = [<FaRobot />, <FaCalculator />, <FaBrain />];
    return icons[index] || <FaFileAlt />;
  };

  const handlePreview = (resource) => {
    const url = resource.previewUrl || resource.downloadUrl;
    if (url && url !== '#') {
      window.open(url, '_blank');
    }
  };

  const handleDownload = (resource) => {
    const url = resource.downloadUrl;
    if (url && url !== '#') {
      const link = document.createElement('a');
      link.href = url;
      link.download = getLocalizedContent(resource, 'title') || 'resource';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const isFr = currentLang === 'fr';

  // Rendered in the loading state too, so the prerendered page always has its meta tags
  const seo = (
    <SEO
      title={isFr ? 'Ressources - Guides & Études de Cas' : 'Resources - Guides & Case Studies'}
      description={isFr
        ? 'Livres blancs, études de cas et guides pratiques sur la RPA, l\'IA et le Low-Code, à télécharger gratuitement pour réussir vos projets d\'automatisation.'
        : 'White papers, case studies and practical guides on RPA, AI and Low-Code, free to download to make your automation projects a success.'
      }
      canonical="/resources"
    />
  );

  if (loading) {
    return (
      <div className="resources">
        {seo}
        <section className="resources-hero">
          <div className="hero-background">
            <img
              src="/assets/images/library-books.jpg"
              alt="Resources"
            />
            <div className="overlay"></div>
          </div>
          <div className="container">
            <motion.div
              className="resources-hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>{t('resources.title')}</h1>
              <p>{t('resources.subtitle')}</p>
            </motion.div>
          </div>
        </section>
        <section className="resources-content">
          <div className="container">
            <Spinner size="medium" text={currentLang === 'fr' ? 'Chargement des ressources...' : 'Loading resources...'} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="resources">
      {seo}
      {/* Hero Section */}
      <section className="resources-hero">
        <div className="hero-background">
          <img
            src="/assets/images/library-books.jpg"
            alt="Resources"
          />
          <div className="overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="resources-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{t('resources.title')}</h1>
            <p>{t('resources.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Resources Content */}
      <section className="resources-content" ref={ref}>
        <div className="container">
          {/* Category Filter */}
          <motion.div
            className="category-filter"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            {categories.map((category) => (
              <button
                key={category.key}
                className={`filter-btn ${activeCategory === category.key ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.key)}
              >
                {category.icon}
                <span>{category.label}</span>
              </button>
            ))}
          </motion.div>

          {/* Resources Grid */}
          <div className="resources-grid">
            {filteredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                className="resource-card"
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <span className="resource-type-badge">
                  {getTypeIcon(getLocalizedContent(resource, 'type'))}
                  {getLocalizedContent(resource, 'type')}
                </span>

                <div className="resource-content">
                  <h3>{getLocalizedContent(resource, 'title')}</h3>
                  <p>{getLocalizedContent(resource, 'description')}</p>

                  <div className="resource-meta">
                    <div className="meta-item">
                      <FaFileAlt />
                      <span>{getLocalizedContent(resource, 'pages')}</span>
                    </div>
                    <div className="meta-item">
                      <FaBrain />
                      <span>{getLocalizedContent(resource, 'readTime')}</span>
                    </div>
                  </div>

                  <div className="resource-actions">
                    <button
                      className="download-btn"
                      onClick={() => handleDownload(resource)}
                      disabled={!resource.downloadUrl || resource.downloadUrl === '#'}
                    >
                      <FaDownload />
                      {currentLang === 'fr' ? 'Télécharger' : 'Download'}
                    </button>
                    <button
                      className="preview-btn"
                      onClick={() => handlePreview(resource)}
                      disabled={(!resource.previewUrl && !resource.downloadUrl) || (resource.previewUrl === '#' && resource.downloadUrl === '#')}
                    >
                      <FaEye />
                      {currentLang === 'fr' ? 'Aperçu' : 'Preview'}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Resources */}
      <section className="popular-resources">
        <div className="container">
          <h2>{currentLang === 'fr' ? 'Ressources les Plus Populaires' : 'Most Popular Resources'}</h2>
          <div className="popular-grid">
            {(popularResources.length > 0 ? popularResources : defaultResources.slice(0, 3)).map((resource, index) => (
              <motion.div
                key={resource.id}
                className="popular-card"
                initial={{ opacity: 0, x: index === 0 ? -30 : index === 2 ? 30 : 0 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => handlePreview(resource)}
                style={{ cursor: 'pointer' }}
              >
                <div className="popular-icon">
                  {getPopularIcon(index)}
                </div>
                <div className="popular-content">
                  <h3>{getLocalizedContent(resource, 'title')}</h3>
                  <p>{getLocalizedContent(resource, 'description').substring(0, 80)}...</p>
                  <span className="download-count">
                    {currentLang === 'fr' ? 'Populaire' : 'Popular'}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
